import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from './common/LoadingSpinner';
import { getUserRoles } from '../lib/auth/llnRoleService';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [checkingInvited, setCheckingInvited] = useState(false);
  const [hasInvitedRole, setHasInvitedRole] = useState(false);

  useEffect(() => {
    const checkInvitedStatus = async () => {
      // Only check invited status if user exists and auth is not loading
      if (!user || loading) {
        setCheckingInvited(false);
        return;
      }

      setCheckingInvited(true);
      try {
        // Get all user roles to check for 'invited' status
        const roles = await getUserRoles(user.id);
        
        // Check if user has 'invited' role
        const isInvited = roles.includes('invited');
        setHasInvitedRole(isInvited);
        
        // Only log in development mode
        if (import.meta.env.DEV) {
          console.log('[ProtectedRoute] User roles:', roles, 'isInvited:', isInvited);
        }
      } catch (error) {
        console.error('[ProtectedRoute] Error checking invited status:', error);
        setHasInvitedRole(false);
      } finally {
        setCheckingInvited(false);
      }
    };

    checkInvitedStatus();
  }, [user, loading]);

  // Show loading state while checking authentication or invited status
  if (loading || checkingInvited) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Define allowed paths for invited users
  const allowedPathsForInvited = ['/set-password', '/reset-password'];
  const isAllowedPath = allowedPathsForInvited.includes(location.pathname);

  // If user has 'invited' role, only allow access to set-password and reset-password
  if (hasInvitedRole && !isAllowedPath) {
    console.log('[ProtectedRoute] Invited user attempting to access restricted path:', location.pathname);
    return <Navigate to="/set-password" replace />;
  }

  // Render children if authenticated and either:
  // - Not an invited user, OR
  // - An invited user on an allowed path
  return <>{children}</>;
};

export default ProtectedRoute;
